import React, { useEffect, useRef, useState } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  Link,
  useParams,
} from 'react-router';
import api from './api';
import HomePage from './components/HomePage';
import LanguageSelection from './components/LanguageSelection';
import Reader from './components/Reader';
import Session from './components/Session';
import LoginModal from './components/LoginModal';
import PasswordChangeModal from './components/PasswordChangeModal';
import './App.css';

function Header({ username, onLogin, onLogout, onPasswordChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuToggleRef = useRef(null);
  const displayUsername = username
    ? username.charAt(0).toUpperCase() + username.slice(1)
    : '';

  const closeMenuAnd = (action) => () => {
    setMenuOpen(false);
    action();
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const closeOnEscapeOrOutsideTap = (event) => {
      if (event.type === 'keydown' && event.key === 'Escape') {
        setMenuOpen(false);
        menuToggleRef.current?.focus();
        return;
      }
      if (event.type === 'pointerdown'
        && !menuRef.current?.contains(event.target)
        && !menuToggleRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', closeOnEscapeOrOutsideTap);
    document.addEventListener('pointerdown', closeOnEscapeOrOutsideTap);
    return () => {
      document.removeEventListener('keydown', closeOnEscapeOrOutsideTap);
      document.removeEventListener('pointerdown', closeOnEscapeOrOutsideTap);
    };
  }, [menuOpen]);

  return (
    <header className="app-header">
      <Link to="/" className="header-title-link" aria-label="Jubilee Translate home" onClick={() => setMenuOpen(false)}>
        <h1>Jubilee Translate</h1>
      </Link>
      {username && (
        <button
          type="button"
          className="mobile-menu-toggle"
          aria-label={menuOpen ? 'Close writer menu' : 'Open writer menu'}
          aria-controls="writer-account-menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
          ref={menuToggleRef}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      )}
      <div
        id={username ? 'writer-account-menu' : undefined}
        className={`auth-buttons${username ? ' authenticated' : ''}${menuOpen ? ' menu-open' : ''}`}
        ref={menuRef}
      >
        {username ? (
          <>
            <span className="username-display" aria-label={`Signed in as ${username}`}>{displayUsername}</span>
            <button type="button" className="btn btn-link" onClick={closeMenuAnd(onPasswordChange)}>Change Password</button>
            <button type="button" className="btn btn-secondary" onClick={closeMenuAnd(onLogout)}>Logout</button>
          </>
        ) : (
          <button type="button" className="btn btn-primary" onClick={onLogin}>Writer Login</button>
        )}
      </div>
    </header>
  );
}

function NotFound() {
  return (
    <section className="page-card not-found" aria-labelledby="not-found-title">
      <h2 id="not-found-title">Page not found</h2>
      <p>This page is no longer part of Jubilee Translate.</p>
      <Link className="btn btn-primary" to="/">Return home</Link>
    </section>
  );
}

function AppShell() {
  const [username, setUsername] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [loginOpen, setLoginOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const refreshProfile = async () => {
    try {
      const response = await api.get('/profile', { validateStatus: (status) => status < 500 });
      setUsername(response.status === 200 ? response.data.username || '' : '');
    } catch (error) {
      setUsername('');
    }
  };

  useEffect(() => {
    refreshProfile().finally(() => setAuthLoading(false));
  }, []);

  const handleLogout = async () => {
    try {
      await api.post('/logout');
      setUsername('');
    } catch (error) {
      // The session is considered gone locally even if the network is down.
      setUsername('');
    }
  };

  if (authLoading) return <div className="loading-container" role="status">Loading Application...</div>;

  return (
    <div className="App">
      <Header
        username={username}
        onLogin={() => setLoginOpen(true)}
        onLogout={handleLogout}
        onPasswordChange={() => setPasswordOpen(true)}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={<HomePage username={username} onLogin={() => setLoginOpen(true)} />} />
          <Route path="/room/:roomCode" element={<LanguageSelection />} />
          <Route path="/room/:roomCode/read" element={<Reader />} />
          <Route path="/session/:roomCode" element={<Session />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/content/:roomCode" element={<LegacyContentRedirect />} />
          <Route path="/join" element={<LegacyJoinRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <LoginModal
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={(signedInUsername) => {
          setUsername(signedInUsername);
          setLoginOpen(false);
        }}
      />
      <PasswordChangeModal isOpen={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </div>
  );
}

function LegacyContentRedirect() {
  const { roomCode } = useParams();
  return <Navigate to={`/room/${roomCode}`} replace />;
}

function LegacyJoinRedirect() {
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
